import type { IAuditLog } from '../../../src/engine/AuditLog';
import { ChangeEngine } from '../../../src/engine/ChangeEngine';
import type { IBatchResult } from '../../../src/models/Operation';
import type { IScanResult } from '../../../src/models/ScanResult';
import { resolveChange } from '../../../src/plan/PlanSimulator';
import { GraphService, type IGraphClient } from '../../../src/services/GraphService';
import { summarizeBatches, UndoEngine } from '../../../src/engine/UndoEngine';
import type { IWorkspace } from '../../api/src/workspace';

export interface IFolderRef {
  id: string;
  name: string;
  parentId: string;
  /** Full path from the top of the library. */
  path: string;
}

function parentOf(path: string): string {
  return path.lastIndexOf('/') === -1 ? '' : path.slice(0, path.lastIndexOf('/'));
}

/** Where a folder would end up, or why it can't go there. Pure, so the picker can grey out bad choices. */
export function moveProblem(folderPath: string, toParentPath: string): string | undefined {
  const from: string = folderPath.toLowerCase();
  const to: string = toParentPath.toLowerCase();
  if (to === parentOf(from)) {
    return 'It is already here.';
  }
  if (to === from || to.indexOf(`${from}/`) === 0) {
    return 'A folder can\'t go inside itself.';
  }
  return undefined;
}

/**
 * Renames one folder through the change engine, so it's checked first (never on top of another
 * folder), logged in History and can be undone.
 */
export async function renameFolder(graph: IGraphClient, w: IWorkspace, audit: IAuditLog, user: string,
  folder: IFolderRef, rootId: string, newName: string): Promise<IBatchResult> {
  return moveFolder(graph, w, audit, user, folder, rootId, parentOf(folder.path), newName, 'Renamed by you.');
}

/**
 * Moves (and optionally renames) one folder into another folder, by path. Missing folders on the
 * way are created. Same checks, History and Undo as every other change.
 */
export async function moveFolder(graph: IGraphClient, w: IWorkspace, audit: IAuditLog, user: string,
  folder: IFolderRef, rootId: string, parentPath: string, newName: string = folder.name, reason: string = 'Moved by you.'): Promise<IBatchResult> {
  const now: string = new Date().toISOString();
  const item = (id: string, name: string, path: string, parentId?: string) => ({
    id, name, path, parentId, depth: path ? path.split('/').length : 0, lastModifiedDateTime: now, webUrl: '',
    childFolderIds: [], fileIds: [], totalFileCount: 0, totalSize: 0
  });
  const scan: IScanResult = {
    library: { id: w.driveId, name: w.libraryName, webUrl: w.webUrl }, rootId,
    folders: { [rootId]: item(rootId, w.libraryName, ''), [folder.id]: item(folder.id, folder.name, folder.path, folder.parentId) },
    files: {}, fileCount: 0, folderCount: 1, totalSize: 0, maxDepth: 1, managedItemCount: 0, protectedFolders: {}, scannedAt: now, durationMs: 0
  };
  const change = {
    id: `restructure:${folder.id}`, type: 'restructure' as const, itemKind: 'folder' as const, targetId: folder.id,
    suggestedParentPath: parentPath, suggestedName: newName, needsInput: false, reason, confidence: 1, issueIds: []
  };
  const resolved = resolveChange(scan, change, { status: 'approved' });
  if (resolved.error) {
    throw new Error(resolved.error);
  }
  const engine = new ChangeEngine({ scan, plan: { changes: [change], quarantinePath: 'Set aside by AI Admin' }, writer: new GraphService(graph).createDriveWriter(w.driveId), audit, user });
  const check = (await engine.dryRun([resolved]))[0];
  if (!check.ready) {
    throw new Error(check.problems.join(' '));
  }
  const batch = await engine.apply([resolved]);
  const failed = batch.entries.filter(e => e.status === 'failed')[0];
  if (failed) {
    throw new Error(failed.errorMessage ?? 'The folder couldn\'t be renamed.');
  }
  return batch;
}

/** Undoes one batch from History (used by the Undo button on toasts and cards). */
export async function undoBatch(graph: IGraphClient, w: IWorkspace, audit: IAuditLog, user: string, batchId: string): Promise<void> {
  const summary = summarizeBatches(await audit.readEntries(w.driveId)).filter(b => b.batchId === batchId)[0];
  if (!summary) {
    throw new Error('That change isn\'t in History any more.');
  }
  const r = await new UndoEngine({ writer: new GraphService(graph).createDriveWriter(w.driveId), audit, libraryId: w.driveId, libraryName: w.libraryName, user }).undo(summary);
  if (r.abortedReason) {
    throw new Error(r.abortedReason);
  }
}
